/* global SideDrawer */

if (!customElements.get('facet-filters')) {
  class FacetFilters extends SideDrawer {
    connectedCallback() {
      this.init();
    }

    disconnectedCallback() {
      clearTimeout(this.filterChangeTimeout);
      this.listenerController?.abort();
      this.applyFiltersFetchAbortController?.abort();
      window.removeEventListener('popstate', this.historyChangeHandler);

      if (this.breakpointChangeHandler) {
        window.removeEventListener('on:breakpoint-change', this.breakpointChangeHandler);
      }
    }

    init() {
      this.filteringEnabled = this.dataset.filtering === 'true';
      this.sortingEnabled = this.dataset.sorting === 'true';
      this.form = document.getElementById('facets');
      this.results = document.getElementById('filter-results');
      this.expanded = [];
      this.filterChangeTimeout = null;
      this.renderedSearchKey = FacetFilters.searchKey(window.location.search);

      this.handleBreakpointChange();
      this.addElements();
      this.addListeners();

      this.historyChangeHandler = this.historyChangeHandler || this.handleHistoryChange.bind(this);
      window.addEventListener('popstate', this.historyChangeHandler);
    }

    addElements() {
      if (this.filteringEnabled) {
        this.filters = this.querySelector('.facets__filters');
        this.activeFilters = this.querySelector('.facets__active-filters');
        this.activeFiltersList = this.querySelector('.active-filters');
        this.activeFiltersHeader = this.querySelector('.active-filters-header');
        this.footer = this.querySelector('.facets__footer');
      }

      if (this.sortingEnabled) {
        this.mobileSortByOptions = this.querySelectorAll('.js-drawer-sort-by');
        this.desktopSortBy = document.querySelector('.products-toolbar__sort');
      }
    }

    addListeners() {
      this.listenerController?.abort();
      this.listenerController = new AbortController();
      const options = { signal: this.listenerController.signal };
      if (this.filteringEnabled) {
        this.breakpointChangeHandler = this.breakpointChangeHandler
          || this.handleBreakpointChange.bind(this);
        this.filters.addEventListener('click', this.handleFiltersClick.bind(this), options);
        this.filters.addEventListener('input', this.handleFilterChange.bind(this), options);
        this.filters.addEventListener('change', this.handleFilterChange.bind(this), options);
        this.activeFilters.addEventListener('click', this.handleActiveFiltersClick.bind(this), options);
        window.addEventListener('on:breakpoint-change', this.breakpointChangeHandler, options);
      }

      if (this.sortingEnabled) {
        this.desktopSortBy.addEventListener('change', this.handleFilterChange.bind(this), options);
      }
    }

    /**
     * Handles viewport breakpoint changes.
     */
    handleBreakpointChange() {
      if (theme.mediaMatches.lg) {
        this.setAttribute('open', '');
        this.setAttribute('aria-hidden', 'false');
        this.removeAttribute('aria-modal');
        this.removeAttribute('role');
      } else {
        this.close();
        this.setAttribute('role', 'dialog');
        this.setAttribute('aria-modal', 'true');
        this.setAttribute('aria-hidden', 'true');
        this.hidden = false;
      }
    }

    /**
     * Handles 'input' events on the filters and 'change' events on the sort by dropdown.
     * @param {object} evt - Event object.
     */
    handleFilterChange(evt) {
      // Only allow price 'change' events
      if (evt.type === 'change' && !(evt.target.id?.includes('price-range') || evt.target.id?.includes('sort-by'))) return;

      // Dont reload when typing a price
      if (evt.target.id?.includes('price-range') && evt.constructor.name === 'InputEvent') return;

      const timeoutDelay = 500;

      clearTimeout(this.filterChangeTimeout);

      this.filterChangeTimeout = setTimeout(() => {
        const formData = new FormData(this.form);
        const searchParams = new URLSearchParams(formData);
        const emptyParams = [];

        if (this.sortingEnabled) {
          let currentSortBy = searchParams.get('sort_by');

          // Keep the mobile facets form sync'd with the desktop sort by dropdown
          if (evt.target.tagName === 'CUSTOM-SELECT') {
            this.mobileSortByOptions.forEach((option) => {
              option.checked = option.value === evt.detail.selectedValue;
              currentSortBy = evt.detail.selectedValue;
            });
          }

          // Set the 'sort_by' parameter.
          searchParams.set('sort_by', currentSortBy);
        }

        // Get empty parameters.
        searchParams.forEach((value, key) => {
          if (!value) emptyParams.push(key);
        });

        // Remove empty parameters.
        emptyParams.forEach((key) => {
          searchParams.delete(key);
        });

        this.applyFilters(searchParams.toString(), evt);
      }, timeoutDelay);
    }

    /**
     * Handles 'click' events on the filters.
     * @param {object} evt - Event object.
     */
    handleFiltersClick(evt) {
      const { target } = evt;

      // Filter 'clear' button clicked.
      if (target.matches('.js-clear-filter')) {
        evt.preventDefault();
        this.applyFilters(new URL(evt.target.href).searchParams.toString(), evt);
      }

      // Filter 'show more' button clicked.
      if (target.matches('.js-show-more')) {
        const filter = target.closest('.filter');
        target.remove();

        filter.querySelectorAll('li').forEach((el) => {
          el.classList.remove('js-hidden');
        });

        if (!this.expanded.includes(filter.id)) {
          this.expanded.push(filter.id);
        }
      }
    }

    /**
     * Handles 'click' events on the active filters.
     * @param {object} evt - Event object.
     */
    handleActiveFiltersClick(evt) {
      const link = evt.target.closest('a');
      if (!link || !this.activeFilters.contains(link) || evt.button !== 0
        || evt.metaKey || evt.ctrlKey || evt.shiftKey || evt.altKey) return;
      evt.preventDefault();
      this.applyFilters(new URL(link.href).searchParams.toString(), evt);
    }

    /**
     * Handles history changes (e.g. back button clicked).
     * @param {object} evt - Event object.
     */
    handleHistoryChange(evt) {
      if (evt.state !== null) {
        let searchParams = '';

        if (evt.state && evt.state.searchParams) {
          ({ searchParams } = evt.state);
        }

        if (FacetFilters.searchKey(searchParams) === this.renderedSearchKey && !this.applyFiltersFetchAbortController) return;
        this.applyFilters(searchParams, null, false);
      }
    }

    /**
     * Fetches the filtered/sorted page data and updates the current page.
     * @param {string} searchParams - Filter/sort search parameters.
     * @param {object} evt - Event object.
     * @param {boolean} [updateUrl=true] - Update url with the selected options.
     */
    async applyFilters(searchParams, evt, updateUrl = true) {
      this.applyFiltersFetchAbortController?.abort();
      const controller = new AbortController();
      this.applyFiltersFetchAbortController = controller;
      const controls = this.closest('[id^="shopify-section-"]')?.querySelector('custom-collection-controls');
      controls?.setBusy?.(true);
      const customPagination = this.closest('custom-pagination');
      const closeBtn = this.querySelector('.js-close-drawer-mob');
      const collectionMemory = customPagination?.hasAttribute('data-collection-memory') ? customPagination : null;
      collectionMemory?.dispatchEvent(new CustomEvent('collection:results-changing', { detail: { fromHistory: !updateUrl } }));
      this.results.querySelector('.js-filter-error')?.remove();
      try {
        // Preserve the current element focus
        const activeElementId = document.activeElement.id;

        // Disable infinite scrolling.
        if (customPagination) {
          customPagination.dataset.pauseInfiniteScroll = 'true';
          customPagination.cancel?.();
        }

        // Set loading state.
        this.results.classList.add('is-loading');

        // Disable "Show X results" button until submission is complete.
        closeBtn?.setAttribute('aria-disabled', 'true');
        closeBtn?.classList.add('is-loading');

        // Use Section Rendering API for the request, while preserving theme preview context.
        const fetchParams = new URLSearchParams(searchParams);
        const previewThemeId = new URL(window.location.href).searchParams.get('preview_theme_id');
        if (previewThemeId) fetchParams.set('preview_theme_id', previewThemeId);
        if (this.form.dataset.filterSectionId) {
          fetchParams.set('section_id', this.form.dataset.filterSectionId);
        }
        const fetchUrl = `${window.location.pathname}?${fetchParams.toString()}`;

        // Fetch filtered products markup.
        const response = await fetch(fetchUrl, {
          method: 'GET',
          signal: controller.signal
        });

        if (!response.ok) throw new Error(`Filter request failed: ${response.status}`);
        if (response.ok) {
          const html = await response.text();
          if (controller.signal.aborted || this.applyFiltersFetchAbortController !== controller || !this.isConnected) return;
          const tmpl = document.createElement('template');
          tmpl.innerHTML = html;
          if (!tmpl.content.getElementById('facets') || !tmpl.content.getElementById('filter-results')) {
            throw new Error('Filter section missing');
          }

          // Restore UI state.
          this.form.querySelectorAll('details-disclosure > details').forEach((existingFilter) => {
            const target = tmpl.content.getElementById(existingFilter.id);
            if (target) {
              target.open = existingFilter.open;
            }
          });
          tmpl.content.querySelectorAll('#facets details-disclosure > details').forEach((newFilter) => {
            if (this.expanded.includes(newFilter.id)) {
              const hiddenElements = newFilter.querySelectorAll('.js-hidden');
              hiddenElements.forEach((listItem) => {
                listItem.classList.remove('js-hidden');
              });
              newFilter.querySelector('.filter__more')?.remove();
            }
          });

          // Update the filters.
          this.form.innerHTML = tmpl.content.getElementById('facets').innerHTML;

          // Update the label of the mobile filter button
          if (closeBtn) closeBtn.innerText = tmpl.content.querySelector('.js-close-drawer-mob')?.innerText || closeBtn.innerText;

          // Preserve the CSS class of the results
          const currentResultsUl = this.results.querySelector('ul');
          this.currentResultsClass = currentResultsUl ? this.results.querySelector('ul').getAttribute('class') : this.currentResultsClass;

          // Update the results.
          this.results.innerHTML = tmpl.content.getElementById('filter-results').innerHTML;

          // Set the CSS class of the results to what it was
          const newResultsUl = this.results.querySelector('ul');
          if (newResultsUl && this.currentResultsClass) newResultsUl.setAttribute('class', this.currentResultsClass);

          // Reinitialize re-rendered components.
          this.addElements();
          this.addListeners();

          // Reinitialize any custom pagination
          if (collectionMemory) {
            const source = tmpl.content.querySelector('[data-collection-memory]');
            collectionMemory.dataset.memoryStartPage = source?.dataset.memoryStartPage || '1';
            collectionMemory.dataset.memoryEndPage = source?.dataset.memoryEndPage || '1';
          }
          if (customPagination && customPagination.reload) customPagination.reload();

          this.renderedSearchKey = FacetFilters.searchKey(searchParams);

          // Update the URL.
          if (updateUrl) FacetFilters.updateURL(searchParams);

          // Scroll to the top of the results if needed
          if ((updateUrl || !collectionMemory) && this.results.getBoundingClientRect().top < 0) {
            // If the header is sticky, compensate for it when scrolling to elements
            let headerHeight = 0;
            if (document.querySelector('store-header[data-is-sticky="true"]')) {
              headerHeight = Number.parseInt(
                getComputedStyle(this.parentElement)
                  .getPropertyValue('--header-height')
                  .replace('px', ''),
                10
              ) || 0;
            }
            window.scrollTo({
              top: this.results.getBoundingClientRect().top + window.scrollY - headerHeight - 45,
              behavior: 'smooth'
            });
          }

          // Enable the "Show X results" button
          closeBtn?.classList.remove('is-loading');
          closeBtn?.removeAttribute('aria-disabled');

          // Renable infinite scroll
          if (customPagination) customPagination.dataset.pauseInfiniteScroll = 'false';

          // Focus on the element with the same ID in the new HTML
          if (activeElementId) document.getElementById(activeElementId)?.focus();

          // Broadcast the update for anything else to hook into
          controls?.update?.(tmpl.content);
          document.dispatchEvent(new CustomEvent('on:facet-filters:updated'), { bubbles: true });
          collectionMemory?.dispatchEvent(new CustomEvent('collection:results-changed', { detail: { fromHistory: !updateUrl } }));
        }
      } catch (error) {
        if (controller.signal.aborted || this.applyFiltersFetchAbortController !== controller) return;
        collectionMemory?.dispatchEvent(new CustomEvent('collection:results-error'));
        const template = customPagination?.querySelector('.js-pagination-feedback');
        const feedback = template?.content.firstElementChild?.cloneNode(true);
        if (feedback) {
          feedback.classList.add('js-filter-error');
          feedback.querySelector('.js-pagination-retry').addEventListener('click', () => {
            this.applyFilters(searchParams, null, updateUrl);
          }, { once: true });
          this.results.prepend(feedback);
        } else {
          console.warn(error);
        }
      } finally {
        if (this.applyFiltersFetchAbortController === controller) {
          controls?.setBusy?.(false);
          this.applyFiltersFetchAbortController = null;
          this.results.classList.remove('is-loading');
          closeBtn?.classList.remove('is-loading');
          closeBtn?.removeAttribute('aria-disabled');
          if (customPagination) customPagination.dataset.pauseInfiniteScroll = 'false';
        }
      }
    }

    /**
     * Updates the url with the current filter/sort parameters.
     * @param {string} searchParams - Filter/sort parameters.
     */
    static searchKey(searchParams) {
      const params = new URLSearchParams(searchParams);
      params.delete('preview_theme_id');
      params.delete('section_id');
      params.sort();
      return params.toString();
    }

    static updateURL(searchParams) {
      const params = new URLSearchParams(searchParams);
      const previewThemeId = new URL(window.location.href).searchParams.get('preview_theme_id');
      if (previewThemeId) params.set('preview_theme_id', previewThemeId);
      const query = params.toString();
      window.history.pushState(
        { searchParams },
        '',
        `${window.location.pathname}${query && '?'.concat(query)}`
      );
    }
  }

  customElements.define('facet-filters', FacetFilters);
}


